import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import type { AuthMe } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { fetchMe, logout } from "@/shared/api/auth";
import { navBadgeLabel } from "@/modules/shell/badge-math";
import {
  CommandMenu,
  useCommandMenuHotkey,
} from "@/modules/shell/CommandMenu";
import {
  deskMoreItemClass,
  deskMoreNavActive,
  deskNavClass,
  deskPrimaryNavActive,
} from "@/modules/shell/desk-nav";
import { useDeskDensity } from "@/modules/shell/density";
import { companyNavForPermissions } from "@/modules/shell/nav-math";
import { DeskNotifPeeks } from "@/modules/shell/DeskNotifPeeks";
import { useShellBadges } from "@/modules/shell/useShellBadges";

/** Company Desk chrome — Shop OS plan/36 + visual plan/35 */
export function CompanyShell() {
  const { companySlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { density, toggleDensity } = useDeskDensity();
  const [me, setMe] = useState<AuthMe | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreMenuPos, setMoreMenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [commandOpen, setCommandOpen] = useState(false);
  const openCommand = useCallback(() => setCommandOpen(true), []);
  const shellHeaderRef = useRef<HTMLElement>(null);
  const moreTriggerRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    const el = shellHeaderRef.current;
    if (!el) return;
    const publish = () => {
      document.documentElement.style.setProperty(
        "--desk-sticky-offset",
        `${el.getBoundingClientRect().height}px`,
      );
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--desk-sticky-offset");
    };
  }, [density, companySlug, me]);

  const placeMoreMenu = useCallback(() => {
    const el = moreTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMoreMenuPos({ top: rect.bottom + 6, left: rect.left });
  }, []);

  useLayoutEffect(() => {
    if (!moreOpen) {
      setMoreMenuPos(null);
      return;
    }
    placeMoreMenu();
    window.addEventListener("resize", placeMoreMenu);
    // Nav strip scrolls horizontally; keep the portaled menu pinned to the trigger.
    window.addEventListener("scroll", placeMoreMenu, true);
    return () => {
      window.removeEventListener("resize", placeMoreMenu);
      window.removeEventListener("scroll", placeMoreMenu, true);
    };
  }, [moreOpen, placeMoreMenu, density]);

  useEffect(() => {
    if (!moreOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMoreOpen(false);
    }
    function onPointer(e: MouseEvent) {
      const t = e.target;
      if (t instanceof Element && t.closest("[data-admin-more]")) return;
      setMoreOpen(false);
    }
    window.addEventListener("keydown", onKey);
    // Avoid closing on the same gesture that opened the menu.
    window.addEventListener("mousedown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
    };
  }, [moreOpen]);

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((data) => {
        if (cancelled) return;
        const allowed =
          data.user.isSuperadmin ||
          data.companies.some((c) => c.slug === companySlug);
        if (!allowed) {
          navigate("/select-company", { replace: true });
          return;
        }
        setMe(data);
      })
      .catch(() => {
        if (!cancelled) navigate("/login", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, navigate]);

  const company = me?.companies.find((c) => c.slug === companySlug);
  const held =
    me?.user.isSuperadmin && !company?.permissions?.length
      ? (["*"] as string[])
      : (company?.permissions ?? []);
  const effectiveHeld = held.length
    ? held
    : me?.user.isSuperadmin
      ? (["*"] as string[])
      : [];
  const { primary, more } = companyNavForPermissions(effectiveHeld);
  const { itemLabel, shellBadgeCount } = useShellBadges({
    companySlug,
    ready: Boolean(me),
    held: effectiveHeld,
  });

  const moreBadge = shellBadgeCount({
    showInbox: more.some((i) => i.id === "inbox"),
    showApprovals: more.some((i) => i.id === "approvals"),
  });
  const morePathActive =
    Boolean(companySlug) &&
    deskMoreNavActive(
      more.map((item) => item.path(companySlug!)),
      location.pathname,
    );

  useCommandMenuHotkey({
    enabled: Boolean(companySlug && me),
    onOpen: openCommand,
  });

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  if (!me) {
    return (
      <div className="flex min-h-dvh items-center justify-center px-4 animate-[fadeIn_240ms_ease-out]">
        <Alert variant="muted" className="max-w-sm">
          Loading company…
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <header
        ref={shellHeaderRef}
        className="sticky top-0 z-20 border-b border-[var(--color-border)]/70 bg-[var(--color-shell)] backdrop-blur-xl supports-[padding:max(0px)]:pt-[max(0.65rem,env(safe-area-inset-top))]"
      >
        <div
          className="mx-auto flex w-full flex-col px-4"
          style={{
            maxWidth: "var(--desk-main-max)",
            paddingTop: "var(--desk-header-pad-y)",
            paddingBottom: "0",
            gap: "0.35rem",
          }}
        >
          <div className="flex items-center justify-between gap-4">
            <Link
              to={companySlug ? `/c/${companySlug}` : "/"}
              className="min-w-0 no-underline"
            >
              <span className="block text-[11px] font-medium tracking-[0.08em] text-[var(--color-muted-fg)]">
                iCraftX
              </span>
              <span className="mt-0.5 block truncate font-[family-name:var(--font-display)] text-[1.35rem] font-semibold leading-tight tracking-tight text-[var(--color-foreground)] sm:text-xl">
                {company?.name ?? companySlug ?? "Company"}
              </span>
            </Link>
            <div className="flex shrink-0 items-center gap-0.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="hidden border-[var(--color-border)]/80 text-[var(--color-muted)] sm:inline-flex"
                title="Command menu (⌘K)"
                onClick={() => setCommandOpen(true)}
              >
                <span className="text-[var(--color-foreground)]">Search</span>
                <kbd className="ml-1.5 rounded border border-[var(--color-border)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-muted)]">
                  ⌘K
                </kbd>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-[var(--color-muted)] sm:hidden"
                title="Command menu"
                onClick={() => setCommandOpen(true)}
              >
                Search
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-[var(--color-muted)]"
                title={
                  density === "everyday"
                    ? "Switch to Power density"
                    : "Switch to Everyday density"
                }
                onClick={toggleDensity}
              >
                {density === "everyday" ? "Everyday" : "Power"}
              </Button>
              <Link
                to="/select-company"
                className="rounded-md px-2.5 py-2 text-sm text-[var(--color-muted)] no-underline transition-colors hover:bg-[var(--color-border)]/35 hover:text-[var(--color-foreground)]"
              >
                Switch
              </Link>
              <button
                type="button"
                className="rounded-md border-0 bg-transparent px-2.5 py-2 text-sm text-[var(--color-muted)] transition-colors hover:bg-[var(--color-border)]/35 hover:text-[var(--color-foreground)]"
                onClick={() => void logout().then(() => navigate("/login"))}
              >
                Sign out
              </button>
            </div>
          </div>
          {companySlug && (primary.length > 0 || more.length > 0) && (
            <nav
              className="-mx-1 flex items-end gap-[var(--desk-nav-gap)] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              aria-label="Company"
              style={{
                paddingBottom: "calc(var(--desk-header-pad-y) * 0.35)",
              }}
            >
              {primary.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.path(companySlug)}
                  className={() =>
                    deskNavClass(
                      deskPrimaryNavActive(
                        item.id,
                        location.pathname,
                        companySlug,
                      ),
                    )
                  }
                >
                  {itemLabel(item.id, item.label)}
                </NavLink>
              ))}
              {more.length > 0 && (
                <div className="relative shrink-0" data-admin-more>
                  <button
                    ref={moreTriggerRef}
                    type="button"
                    className={deskNavClass(moreOpen || morePathActive)}
                    aria-expanded={moreOpen}
                    aria-haspopup="menu"
                    aria-current={morePathActive ? "page" : undefined}
                    onClick={() => {
                      const el = moreTriggerRef.current;
                      if (!moreOpen && el) {
                        const rect = el.getBoundingClientRect();
                        setMoreMenuPos({
                          top: rect.bottom + 6,
                          left: Math.min(
                            rect.left,
                            window.innerWidth - 220,
                          ),
                        });
                      }
                      setMoreOpen((v) => !v);
                    }}
                  >
                    {navBadgeLabel("More", moreBadge)}
                  </button>
                  {/*
                    Portaled: desk nav uses overflow-x-auto, which clips absolute
                    children (CSS treats the other axis as auto too). This is the
                    primary nav “More” (not row ⋯ actions).
                  */}
                  {moreOpen &&
                    moreMenuPos &&
                    createPortal(
                      <div
                        data-admin-more
                        role="menu"
                        aria-label="More"
                        className="fixed z-[80] min-w-[13rem] rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-card)] p-1.5 shadow-[var(--shadow-float)] animate-[fadeIn_160ms_ease-out]"
                        style={{
                          top: moreMenuPos.top,
                          left: Math.max(8, moreMenuPos.left),
                        }}
                      >
                        {more.map((item) => (
                          <NavLink
                            key={item.id}
                            to={item.path(companySlug)}
                            end
                            role="menuitem"
                            onClick={() => setMoreOpen(false)}
                            className={({ isActive }) =>
                              deskMoreItemClass(
                                isActive ||
                                  deskMoreNavActive(
                                    [item.path(companySlug)],
                                    location.pathname,
                                  ),
                              )
                            }
                          >
                            {itemLabel(item.id, item.label)}
                          </NavLink>
                        ))}
                      </div>,
                      document.body,
                    )}
                </div>
              )}
            </nav>
          )}
        </div>
      </header>
      <main
        className="mx-auto w-full flex-1 px-4 animate-[fadeIn_280ms_ease-out]"
        style={{
          maxWidth: "var(--desk-main-max)",
          paddingTop: "var(--desk-page-y)",
          paddingBottom: "max(2.5rem, env(safe-area-inset-bottom))",
        }}
      >
        <Outlet />
      </main>
      {companySlug ? (
        <CommandMenu
          companySlug={companySlug}
          held={effectiveHeld}
          open={commandOpen}
          onOpenChange={setCommandOpen}
        />
      ) : null}
      {companySlug && me ? (
        <DeskNotifPeeks companySlug={companySlug} enabled />
      ) : null}
    </div>
  );
}
